import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { ConfiguredJob } from "common/state/rtcJobsApi";
import { DateTime } from "luxon";
import { farmApi } from "common/state/portalApi/farm";
import { getFarmPath, getRigDetailsPath } from "portal/utils/routing";
import { Job as JobProto, State } from "protos/rtc/jobs";
import { JobType } from "protos/atk/job_type";
import {
  jobTypeToLocaleString,
  stateToLocaleString,
} from "common/utils/enumTranslations";
import { Link } from "react-router-dom";
import { portalApi } from "portal/state/portalApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { SummaryCardItemProps } from "portal/components/SummaryCardItem";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { userApi } from "common/state/portalApi/user";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import AccessTimeIcon from "@mui/icons-material/AccessTimeOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomerIcon from "portal/images/icons/farm.svg?react";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import FieldIcon from "portal/images/icons/field.svg?react";
import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import JobIcon from "@mui/icons-material/AssignmentOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNextOutlined";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import PersonIcon from "@mui/icons-material/AccountCircleOutlined";
import React, { FC, ReactNode } from "react";
import RigIcon from "@mui/icons-material/Agriculture";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

const EditJobDialogActionButton: FC<{ openEditJobDialog: () => void }> = ({
  openEditJobDialog,
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip
      title={t("utils.actions.editLong", { subject: t("models.jobs.job_one") })}
      arrow
    >
      <IconButton className="w-8 h-8" size="medium" onClick={openEditJobDialog}>
        <EditIcon style={{ color: "inherit", fontSize: "inherit" }} />
      </IconButton>
    </Tooltip>
  );
};

export function useCustomerStatus(job: JobProto): SummaryCardItemProps[] {
  const { t } = useTranslation();
  const { isLoading, data: customers } = useQueryPopups(
    portalApi.endpoints.listCustomers.useQuery()
  );

  const customerIdNumber = Number.parseInt(job.customerId);
  const customer = Number.isNaN(customerIdNumber)
    ? undefined
    : customers?.find((customer) => customer.db?.id === customerIdNumber);
  const customerText = customer
    ? t("views.autotractor.jobDetails.customer.withName", {
        name: customer.name,
      })
    : t("views.autotractor.jobDetails.customer.unknown");

  return [
    {
      icon: <CustomerIcon />,
      text: isLoading ? t("components.Loading.placeholder") : customerText,
    },
  ];
}

export function useJobCreatorStatus(job: JobProto): SummaryCardItemProps[] {
  const { t } = useTranslation();
  const { isInternal } = useSelf();
  const { data, isLoading } = userApi.endpoints.getUser.useQuery(
    isInternal && job.createdBy ? { userId: job.createdBy } : skipToken
  );

  // Only show creator internally
  if (!isInternal || !job.createdBy) {
    return [];
  }

  const name = data?.user?.name ?? data?.user?.email ?? job.createdBy;

  return [
    {
      icon: <PersonIcon />,
      text: isLoading
        ? t("components.Loading.placeholder")
        : t("views.autotractor.jobDetails.creator.withName", { name }),
    },
  ];
}

export function useFarmStatus(
  job: JobProto,
  openEditJobDialog: (() => void) | undefined
): SummaryCardItemProps[] {
  const { t } = useTranslation();
  const { isLoading, data: farm } = useQueryPopups(
    farmApi.endpoints.getFarm.useQuery(job.farmId)
  );

  const editFieldActions = openEditJobDialog
    ? [
        <EditJobDialogActionButton
          key="edit"
          openEditJobDialog={openEditJobDialog}
        />,
      ]
    : [];

  if (farm) {
    const farmLinkOutButton = farm.id && (
      <Tooltip
        key="link"
        title={t("views.autotractor.jobDetails.openInFarmView")}
        arrow
      >
        <IconButton
          className="w-8 h-8"
          size="medium"
          component={Link}
          to={getFarmPath(farm.id.id)}
        >
          <NavigateNextIcon style={{ color: "inherit", fontSize: "inherit" }} />
        </IconButton>
      </Tooltip>
    );
    const farmProps: SummaryCardItemProps = {
      icon: <CustomerIcon />,
      text: t("views.autotractor.jobDetails.farm.withName", {
        name: farm.name,
      }),
      actions: farmLinkOutButton ? [farmLinkOutButton] : [],
    };

    const zone =
      job.fieldId && farm.zones.find((zone) => zone.id?.id === job.fieldId);

    // Transport jobs don't have fields
    if (job.type === JobType.TRANSPORT) {
      return [farmProps];
    }

    if (zone) {
      const fieldLinkOutButton = farm.id && (
        <Tooltip
          key="link"
          title={t("views.autotractor.jobDetails.openInFarmView")}
          arrow
        >
          <IconButton
            className="w-8 h-8"
            size="medium"
            component={Link}
            to={getFarmPath(
              farm.id.id,
              zone.id && { type: "zone", id: zone.id.id }
            )}
          >
            <NavigateNextIcon style={{ fontSize: "inherit" }} />
          </IconButton>
        </Tooltip>
      );

      return [
        farmProps,
        {
          icon: <FieldIcon width={24} />,
          text: t("views.autotractor.jobDetails.field.withName", {
            name: zone.name,
          }),
          actions: fieldLinkOutButton
            ? [...editFieldActions, fieldLinkOutButton]
            : editFieldActions,
        },
      ];
    } else {
      return [
        farmProps,
        {
          icon: <FieldIcon width={24} />,
          text: t("views.autotractor.jobDetails.field.unknown"),
          actions: editFieldActions,
        },
      ];
    }
  } else {
    return [
      {
        icon: <CustomerIcon />,
        text: isLoading
          ? t("components.Loading.placeholder")
          : t("views.autotractor.jobDetails.farm.unknown"),
        actions: [],
      },
      {
        icon: <FieldIcon width={24} />,
        text: isLoading
          ? t("components.Loading.placeholder")
          : t("views.autotractor.jobDetails.field.unknown"),
        actions: editFieldActions,
      },
    ];
  }
}

export function useJobTypeStatus(
  job: JobProto,
  openEditJobDialog: (() => void) | undefined
): SummaryCardItemProps[] {
  const { t } = useTranslation();
  const typeText = jobTypeToLocaleString(t, job.type);

  return [
    {
      icon: <JobIcon />,
      text: t("views.autotractor.jobDetails.type", {
        type: typeText,
      }),
      actions: openEditJobDialog
        ? [
            <EditJobDialogActionButton
              key="edit"
              openEditJobDialog={openEditJobDialog}
            />,
          ]
        : [],
    },
  ];
}

export function useRigStatus(
  configuredJob: ConfiguredJob
): SummaryCardItemProps[] {
  const { t } = useTranslation();
  const { data: getRigResponse, isLoading: isRigLoading } = useQueryPopups(
    atkEquipmentApi.endpoints.getRig.useQuery(
      { id: configuredJob.groundPrep?.rigId ?? "" },
      { skip: !configuredJob.groundPrep?.rigId }
    )
  );

  const items: SummaryCardItemProps[] = [];

  if (configuredJob.groundPrep?.rigId) {
    const rigLinkOutButton = (
      <Tooltip
        key="link"
        title={t("views.autotractor.jobDetails.equipmentSetupLinkOutTooltip")}
        arrow
      >
        <IconButton
          className="w-8 h-8"
          size="medium"
          component={Link}
          to={getRigDetailsPath(configuredJob.groundPrep.rigId)}
        >
          <NavigateNextIcon style={{ color: "inherit", fontSize: "inherit" }} />
        </IconButton>
      </Tooltip>
    );

    if (isRigLoading) {
      items.push({
        icon: <CircularProgress size={24} variant="indeterminate" />,
        text: t("views.autotractor.jobDetails.equipmentSetup.loading"),
      });
    } else if (getRigResponse) {
      items.push({
        icon: <RigIcon />,
        text: t("views.autotractor.jobDetails.equipmentSetup.withName", {
          name: getRigResponse.rig?.customerProvidedName,
        }),
        actions: [rigLinkOutButton],
      });
    } else {
      items.push({
        icon: <RigIcon />,
        text: t("views.autotractor.jobDetails.equipmentSetup.error", {
          rigId: configuredJob.groundPrep.rigId,
        }),
        actions: [rigLinkOutButton],
      });
    }
  }

  return items;
}

export function useJobStateStatus(job: JobProto): SummaryCardItemProps[] {
  const { t } = useTranslation();

  let icon: ReactNode | undefined;
  const stateText = stateToLocaleString(t, job.state);

  switch (job.state) {
    case State.PENDING: {
      icon = <HourglassEmptyIcon />;
      break;
    }
    case State.READY: {
      icon = <TaskAltIcon />;
      break;
    }
    case State.IN_PROGRESS: {
      icon = <AutorenewIcon className="animate-spin" />;
      break;
    }
    case State.COMPLETED: {
      icon = <CheckCircleIcon />;
      break;
    }
    case State.CANCELLED: {
      icon = <CancelIcon />;
      break;
    }
    case State.PAUSED: {
      icon = <PauseCircleFilledIcon />;
      break;
    }
    case State.FAILED: {
      icon = <ErrorIcon />;
      break;
    }
    case State.ACKNOWLEDGED: {
      icon = <DoneAllIcon />;
      break;
    }
    case State.NEW: {
      icon = <FiberNewIcon />;
      break;
    }
    default: /* UNRECOGNIZED, STATE_UNSPECIFIED */ {
      icon = <HelpOutlinedIcon />;
      break;
    }
  }

  return [
    {
      icon,
      text: t("views.autotractor.jobDetails.state", { state: stateText }),
    },
  ];
}

export function useJobTimesStatus(job: JobProto): SummaryCardItemProps[] {
  const { t } = useTranslation();
  const items: SummaryCardItemProps[] = [];
  const startDate = job.startedAt ? DateTime.fromISO(job.startedAt) : undefined;
  const endDate = job.endedAt ? DateTime.fromISO(job.endedAt) : undefined;

  if (startDate) {
    items.push({
      icon: <AccessTimeIcon />,
      text: t("views.autotractor.jobDetails.jobStarted", {
        time: startDate.toLocaleString(DateTime.DATETIME_SHORT),
      }),
    });
  }

  if (endDate) {
    items.push({
      icon: <AccessTimeIcon />,
      text: t("views.autotractor.jobDetails.jobFinished", {
        time: endDate.toLocaleString(DateTime.DATETIME_SHORT),
      }),
    });
  }

  return items;
}
